import { useMemo, useState } from 'react'
import type { ChartCanvasProps, BlockerGraphData } from './chartTypes'
import { chartColor } from './visxPrimitives'
import { TooltipCard } from './TooltipCard'

type Active = { id: string; label: string; value: number; color: string; x: number; y: number } | null

export const BlockerGraphChart = ({ data, width, height }: ChartCanvasProps<BlockerGraphData>) => {
  const [active, setActive] = useState<Active>(null)
  const centerX = width / 2
  const centerY = height / 2
  const radius = Math.max(40, Math.min(width, height) / 2 - 60)
  const positions = useMemo(() => new Map(data.nodes.map((node, index) => {
    const angle = (index / Math.max(1, data.nodes.length)) * Math.PI * 2 - Math.PI / 2
    return [node.id, {
      x: data.nodes.length === 1 ? centerX : centerX + Math.cos(angle) * radius,
      y: data.nodes.length === 1 ? centerY : centerY + Math.sin(angle) * radius,
    }]
  })), [centerX, centerY, data.nodes, radius])

  return (
    <div className="analysisChartCanvas analysisChartCanvas--center">
      <svg className="analysisChartSvg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Blocker graph">
        <title>Blocker graph</title>
        {data.links.map((link) => {
          const source = positions.get(link.source)
          const target = positions.get(link.target)
          if (!source || !target) return null
          return (
            <line
              key={`${link.source}-${link.target}`}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              stroke="var(--chart-grid)"
              strokeWidth={1.4}
              strokeDasharray="4 4"
            />
          )
        })}
        {data.nodes.map((node, index) => {
          const position = positions.get(node.id) ?? { x: centerX, y: centerY }
          const color = chartColor(index)
          const r = Math.max(14, Math.min(30, 12 + node.value * 4))
          const isActive = active?.id === node.id
          return (
            <g key={node.id} transform={`translate(${position.x} ${position.y})`}>
              <circle
                r={r}
                fill={color}
                fillOpacity={isActive ? 0.95 : 0.32}
                stroke={color}
                strokeWidth={isActive ? 3 : 2}
                tabIndex={0}
                role="button"
                aria-label={`${node.label}: ${node.value} signal${node.value === 1 ? '' : 's'}`}
                onPointerEnter={() => setActive({ id: node.id, label: node.label, value: node.value, color, x: position.x, y: position.y - r })}
                onPointerLeave={() => setActive(null)}
                onFocus={() => setActive({ id: node.id, label: node.label, value: node.value, color, x: position.x, y: position.y - r })}
                onBlur={() => setActive(null)}
              />
              <text
                y="4"
                textAnchor="middle"
                fill="var(--text-secondary)"
                fontSize="12"
                fontWeight={700}
                pointerEvents="none"
              >
                {node.value}
              </text>
            </g>
          )
        })}
      </svg>
      {active && (
        <TooltipCard
          left={active.x}
          top={active.y}
          accent={active.color}
          title={active.label}
          value={`${active.value} chain node${active.value === 1 ? '' : 's'}`}
        />
      )}
    </div>
  )
}
